import { useEffect, useMemo, FC, Fragment, MouseEvent, useState } from "react";

// material-ui
import { alpha, useTheme } from "@mui/material/styles";
import {
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  useMediaQuery,
  Stack,
  Table,
  Box,
  Skeleton,
} from "@mui/material";

// third-party
import {
  useFilters,
  useExpanded,
  useGlobalFilter,
  useRowSelect,
  useSortBy,
  useTable,
  usePagination,
  Column,
  HeaderGroup,
  Row,
  Cell,
} from "react-table";

// project-imports
import {
  EmptyTable,
  HeaderSort,
  SortingSelect,
  TablePagination,
} from "components/third-party/ReactTable";

// import makeData from 'data/react-table';
import { renderFilterTypes, GlobalFilter } from "utils/react-table";
import { PaginationState } from "types/common";
import MainCard from "components/MainCard";
import ScrollX from "components/ScrollX";
import { Typography } from "@mui/material";

// types

// ==============================|| REACT TABLE ||============================== //
interface Props {
  columns: Column[];
  data: any[];
  renderRowSubComponent: FC<any>;
  colSpan: number;
}

export function ReactTable({
  columns,
  data,
  renderRowSubComponent,
  colSpan,
}: Props) {
  const theme = useTheme();
  const matchDownSM = useMediaQuery(theme.breakpoints.down("sm"));

  const filterTypes = useMemo(() => renderFilterTypes, []);
  const sortBy = { id: "id", desc: false };

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    setHiddenColumns,
    allColumns,
    visibleColumns,
    rows,
    page,
    gotoPage,
    setPageSize,
    state: { globalFilter, pageIndex, pageSize, expanded },
    preGlobalFilteredRows,
    setGlobalFilter,
    setSortBy,
  } = useTable(
    {
      columns,
      data,
      filterTypes,
      initialState: {
        pageIndex: 0,
        pageSize: 10,
        hiddenColumns: ["avatar", "email"],
        sortBy: [sortBy],
      },
    },
    useGlobalFilter,
    useFilters,
    useSortBy,
    useExpanded,
    usePagination,
    useRowSelect
  );

  useEffect(() => {
    if (matchDownSM) {
      setHiddenColumns([
        "age",
        "contact",
        "visits",
        "email",
        "status",
        "avatar",
      ]);
    } else {
      setHiddenColumns(["avatar", "email"]);
    }
    // eslint-disable-next-line
  }, [matchDownSM]);

  return (
    <>
      <Stack spacing={3}>
        <Stack
          direction={matchDownSM ? "column" : "row"}
          spacing={1}
          justifyContent="space-between"
          alignItems="center"
          sx={{ p: 3, pb: 0 }}
        >
          <GlobalFilter
            sx={{ width: 200 }}
            preGlobalFilteredRows={preGlobalFilteredRows}
            globalFilter={globalFilter}
            setGlobalFilter={setGlobalFilter}
          />
          <Stack
            direction={matchDownSM ? "column" : "row"}
            alignItems="center"
            spacing={2}
          >
            <SortingSelect
              sortBy={sortBy.id}
              setSortBy={setSortBy}
              allColumns={allColumns}
            />
          </Stack>
        </Stack>
        <Stack
          direction={matchDownSM ? "column" : "row"}
          spacing={1}
          justifyContent="space-between"
          alignItems="center"
          sx={{ pl: 2, pr: 2, pb: 0 }}
        ></Stack>

        <Table {...getTableProps()}>
          <TableHead>
            {headerGroups?.map((headerGroup: HeaderGroup<{}>) => (
              <TableRow
                {...headerGroup.getHeaderGroupProps()}
                sx={{ "& > th:first-of-type": { width: "58px" } }}
              >
                {headerGroup.headers?.map((column: HeaderGroup) => (
                  <TableCell
                    {...column.getHeaderProps([
                      { className: column.className },
                    ])}
                  >
                    <HeaderSort column={column} sort />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>
          <TableBody {...getTableBodyProps()}>
            {page?.map((row: Row, i: number) => {
              prepareRow(row);
              const rowProps = row.getRowProps();
              return (
                <Fragment key={i}>
                  <TableRow
                    {...row.getRowProps()}
                    sx={{
                      cursor: "pointer",
                      bgcolor: row.isSelected
                        ? alpha(theme.palette.primary.lighter, 0.35)
                        : "inherit",
                    }}
                    onClick={(e: MouseEvent<HTMLTableRowElement>) => {
                      e.stopPropagation();
                      row.toggleRowExpanded();
                    }}
                  >
                    {row?.cells?.map((cell: Cell) => (
                      <TableCell
                        {...cell.getCellProps([
                          { className: cell.column.className },
                        ])}
                      >
                        {cell.render("Cell")}
                      </TableCell>
                    ))}
                  </TableRow>
                  {row.isExpanded &&
                    renderRowSubComponent({
                      row,
                      rowProps,
                      visibleColumns,
                      expanded,
                    })}
                </Fragment>
              );
            })}
            {data.length > 0 ? (
              <TableRow
                sx={{ "&:hover": { bgcolor: "transparent !important" } }}
              >
                <TableCell sx={{ p: 2, py: 3 }} colSpan={colSpan}>
                  <TablePagination
                    gotoPage={gotoPage}
                    rows={rows}
                    setPageSize={setPageSize}
                    pageSize={pageSize}
                    pageIndex={pageIndex}
                  />
                </TableCell>
              </TableRow>
            ) : (
              <EmptyTable msg="No Orders Found" colSpan={colSpan} />
            )}
          </TableBody>
        </Table>
      </Stack>
    </>
  );
}

function ReactTableBasic({
  columns,
  data,
  striped,
  emptyMessage,
  pageIndex,
  setPageIndex,
}: {
  columns: Column[];
  data: any[];
  striped?: boolean;
  emptyMessage: string;
  setPageIndex: (val: number) => void;
  pageIndex: number;
}) {
  const theme = useTheme();

  const {
		getTableProps,
		getTableBodyProps,
		state: { pageSize },
		headerGroups,
		rows,
		prepareRow,
		page,
		gotoPage,
		setPageSize,
	} = useTable(
		{
			columns,
			data,
			initialState: {
				pageIndex,
				pageSize: 10,
				hiddenColumns: ["t1Quantity"],
			},
		},
		usePagination
	);

	useEffect(() => {
		gotoPage(pageIndex);
	}, [pageIndex, gotoPage]);

	const handleChangePage = (newPage: number) => {
		setPageIndex(newPage);
	};

	return (
		<Table {...getTableProps()} sx={{ border: "none" }}>
			<TableHead>
				{headerGroups?.map((headerGroup: HeaderGroup<{}>) => {
					const { key, ...rowProps } = headerGroup.getHeaderGroupProps();
					return (
						<TableRow
							{...rowProps}
							key={key}
							sx={{ "& > th:first-of-type": { width: "58px" } }}>
							{headerGroup.headers.map((column: HeaderGroup) => {
								const { key, ...headerProps } = column.getHeaderProps([
									{ className: column.className },
								]);
								return (
									<TableCell key={column.id} sx={{ p: 0.9 }} {...headerProps}>
										{column.render("Header")}
									</TableCell>
								);
							})}
						</TableRow>
					);
				})}
			</TableHead>
			<TableBody {...getTableBodyProps()}>
				{page && page.length > 0 ? (
					page.map((row: Row) => {
						prepareRow(row);
						const { key, ...rowProps } = row.getRowProps();
						return (
							<TableRow
								key={key}
								{...rowProps}
								sx={{
									cursor: "pointer",
									bgcolor: row.isSelected
										? alpha(theme.palette.primary.lighter, 0.35)
										: "inherit",
								}}>
								{row.cells.map((cell: Cell) => {
									const { key, ...cellProps } = cell.getCellProps([
										{ className: cell.column.className },
									]);
									const cellKey = `${cell.row.id}-${cell.column.id}`;
									return (
										<TableCell key={cellKey} sx={{ p: 0.8 }} {...cellProps}>
											{cell.render("Cell")}
										</TableCell>
									);
								})}
							</TableRow>
						);
					})
				) : (
					<EmptyTable msg={emptyMessage} colSpan={columns.length ?? 0} />
				)}
				{rows.length > 9 && (
					<TableRow key="pagination-row">
						<TableCell colSpan={columns.length} sx={{ p: 0.9 }}>
							<TablePagination
								gotoPage={handleChangePage}
								rows={rows}
								setPageSize={setPageSize}
								pageSize={pageSize}
								pageIndex={pageIndex}
							/>
						</TableCell>
					</TableRow>
				)}
			</TableBody>
		</Table>
	);
}

export const BasicTable = ({
  data,
  striped,
  title,
  columns,
  emptyMessage,
  actionButtonSection,
  isLoading,
  loadingText,
}: {
  data: any[];
  striped?: boolean;
  title?: string;
  columns: Column[];
  emptyMessage: string;
  actionButtonSection?: React.ReactNode;
  isLoading: boolean;
  loadingText: string;
}) => {
  const [pageIndex, setPageIndex] = useState<number>(0);
  return (
    <MainCard content={false}>
      <ScrollX>
        <Stack spacing={3}>
          <Box sx={{ p: 3, pb: 0 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1}
            >
              <Typography variant="h5">{title}</Typography>
              {actionButtonSection && actionButtonSection}
            </Stack>
          </Box>

          <ReactTableBasic
            pageIndex={pageIndex}
            setPageIndex={setPageIndex}
            columns={columns}
            data={data}
            striped={striped}
            emptyMessage={isLoading ? loadingText : emptyMessage}
          />
        </Stack>
      </ScrollX>
    </MainCard>
  );
};

interface TableProps {
  columns: Column[];
  data: any[];
  emptyTableMessage: string;
  pageState: PaginationState;
  setPage: (val: PaginationState) => void;
  totalNumberOfRecords: number;
}

export function ReactTableWithPage({
  columns,
  data,
  emptyTableMessage,
  pageState,
  setPage,
  totalNumberOfRecords,
}: TableProps) {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    page,
    state: { pageIndex },
  } = useTable(
    {
      columns,
      data,
      initialState: {
        pageIndex: pageState.page,
        pageSize: pageState.limit,
        hiddenColumns: ["avatar", "quantity"],
      },
      manualPagination: true,
      pageCount: Math.ceil(totalNumberOfRecords / pageState.limit),
    },
    useGlobalFilter,
    useFilters,
    useSortBy,
    usePagination
  );

  const handlePageChange = (pageIndex: number) => {
    setPage({ ...pageState, page: pageIndex });
  };

  const handlePageSizeChange = (pageSize: number) => {
    setPage({ ...pageState, limit: pageSize, page: 0 });
  };

  return (
    <>
      <Stack spacing={3}>
        <Table {...getTableProps()}>
          <TableHead>
            {headerGroups.map((headerGroup: HeaderGroup<{}>) => {
              const { key, ...restProps } = headerGroup.getHeaderGroupProps();
              return (
                <TableRow {...restProps} key={key}>
                  {headerGroup.headers.map((column: HeaderGroup<{}>) => {
                    const { key, ...restProps } = column.getHeaderProps([
                      { className: column.className },
                    ]);
                    return (
                      <TableCell
                        {...restProps}
                        sx={{ py: 2, px: 0.6 }}
                        align="center"
                        key={key}
                      >
                        {column.render("Header")}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableHead>
          <TableBody {...getTableBodyProps()}>
            {page.length > 0 ? (
              <>
                {page.map((row: Row, i: number) => {
                  prepareRow(row);
                  const { key, ...restProps } = row.getRowProps();
                  return (
                    <Fragment key={i}>
                      <TableRow {...restProps} key={key}>
                        {row.cells.map((cell: Cell<{}>) => {
                          const { key, ...restProps } = cell.getCellProps([
                            { className: cell.column.className },
                          ]);
                          return (
                            <TableCell
                              {...restProps}
                              key={key}
                              align="center"
                              sx={{ py: 1, px: 0.5 }}
                            >
                              {cell.render("Cell")}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    </Fragment>
                  );
                })}
              </>
            ) : (
              <EmptyTable msg={emptyTableMessage} colSpan={columns.length} />
            )}
            <TableRow sx={{ "&:hover": { bgcolor: "transparent !important" } }}>
              <TableCell sx={{ p: 2, py: 3 }} colSpan={columns.length}>
                <TablePagination
                  totalPages={Math.ceil(totalNumberOfRecords / pageState.limit)}
                  gotoPage={handlePageChange}
                  rows={rows}
                  setPageSize={handlePageSizeChange}
                  pageSize={pageState.limit}
                  pageIndex={pageIndex}
                  initialPageSize={pageState.limit}
                />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Stack>
    </>
  );
}

export function ReactTableNoPagination<T extends object>({
  columns,
  data,
  striped,
  emptyMessage,
}: {
  columns: Column<T>[];
  data: T[];
  striped?: boolean;
  emptyMessage: string;
}) {
  const theme = useTheme();

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable<T>({
      columns,
      data,
      initialState: { hiddenColumns: ["t1Quantity"] },
    });

  return (
    <Table {...getTableProps()} sx={{ border: "none" }}>
      <TableHead>
        {headerGroups.map((headerGroup: HeaderGroup<T>) => (
          <TableRow
            {...headerGroup.getHeaderGroupProps()}
            sx={{ "& > th:first-of-type": { width: "58px" } }}
          >
            {headerGroup.headers.map((column: HeaderGroup<T>) => (
              <TableCell
                sx={{ p: 0.9 }}
                {...column.getHeaderProps([{ className: column.className }])}
              >
                {column.render("Header")}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableHead>
      <TableBody {...getTableBodyProps()}>
        {rows.length > 0 ? (
          rows.map((row: Row<T>, i: number) => {
            prepareRow(row);
            return (
              <Fragment key={i}>
                <TableRow
                  {...row.getRowProps()}
                  sx={{
                    cursor: "pointer",
                    bgcolor: row.isSelected
                      ? alpha(theme.palette.primary.lighter, 0.35)
                      : "inherit",
                  }}
                >
                  {row.cells.map((cell: Cell<T>) => (
                    <TableCell
                      sx={{ p: 0.8 }}
                      {...cell.getCellProps([
                        { className: cell.column.className },
                      ])}
                    >
                      {cell.render("Cell")}
                    </TableCell>
                  ))}
                </TableRow>
              </Fragment>
            );
          })
        ) : (
          <EmptyTable msg={emptyMessage} colSpan={columns.length ?? 0} />
        )}
      </TableBody>
    </Table>
  );
}

interface ReactTableWithSubTableProps {
  columns: Column[];
  data: any[];
  renderRowSubComponent: FC<any>;
  colSpan: number;
  rowClick: boolean;
  title: string;
  emptyTableMessage: string;
  isLoading: boolean;
  loadingMessage: string;
}

export function ReactTableWithSubTable({
  columns,
  data,
  renderRowSubComponent,
  colSpan,
  rowClick,
  title,
  emptyTableMessage,
  loadingMessage,
  isLoading,
}: ReactTableWithSubTableProps) {
  const theme = useTheme();
  const matchDownSM = useMediaQuery(theme.breakpoints.down("sm"));

  const filterTypes = useMemo(() => renderFilterTypes, []);
  const sortBy = { id: "id", desc: false };

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    setHiddenColumns,
    visibleColumns,
    rows,
    page,
    gotoPage,
    setPageSize,
    state: { pageIndex, pageSize, expanded },
  } = useTable(
    {
      columns,
      data,
      filterTypes,
      initialState: {
        pageIndex: 0,
        pageSize: 10,
        hiddenColumns: ["avatar", "email", "requestId", "stockId"],
        sortBy: [sortBy],
      },
    },
    useGlobalFilter,
    useFilters,
    useSortBy,
    useExpanded,
    usePagination,
    useRowSelect
  );

  useEffect(() => {
    if (matchDownSM) {
      setHiddenColumns([
        "age",
        "contact",
        "visits",
        "email",
        "status",
        "avatar",
        "requestId",
      ]);
    } else {
      setHiddenColumns(["avatar", "email", "requestId"]);
    }
    // eslint-disable-next-line
  }, [matchDownSM]);

  return (
    <MainCard content={false}>
      <ScrollX>
        <Stack spacing={3}>
          <Box sx={{ p: 3, pb: 0 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1}
            >
              <Typography variant="h5">{title}</Typography>
            </Stack>
          </Box>

          <Table {...getTableProps()}>
            <TableHead>
              {headerGroups.map((headerGroup: HeaderGroup<{}>) => {
                const { key, ...restProps } = headerGroup.getHeaderGroupProps();
                return (
                  <TableRow
                    {...restProps}
                    key={key}
                    sx={{ "& > th:first-of-type": { width: "58px" } }}
                  >
                    {headerGroup.headers.map((column: HeaderGroup) => {
                      const { key, ...restProps } = column.getHeaderProps([
                        { className: column.className },
                      ]);
                      return (
                        <TableCell sx={{ p: 0.6 }} {...restProps} key={key}>
                          {column.render("Header")}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableHead>
            <TableBody {...getTableBodyProps()}>
              {page.map((row: Row, i: number) => {
                prepareRow(row);
                const rowProps = row.getRowProps();
                const { key, ...restProps } = rowProps;
                return (
                  <Fragment key={i}>
                    <TableRow
                      {...restProps}
                      key={key}
                      sx={{ cursor: "pointer" }}
                      onClick={(e: MouseEvent<HTMLTableRowElement>) => {
                        if (rowClick) {
                          e.stopPropagation();
                          row.toggleRowExpanded();
                        }
                      }}
                    >
                      {row.cells.map((cell: Cell) => {
                        const { key, ...restProps } = cell.getCellProps([
                          { className: cell.column.className },
                        ]);
                        return (
                          <TableCell sx={{ p: 0.5 }} {...restProps} key={key}>
                            {cell.render("Cell")}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                    {row.isExpanded &&
                      renderRowSubComponent({
                        row,
                        rowProps,
                        visibleColumns,
                        expanded,
                      })}
                  </Fragment>
                );
              })}
              {data.length > 0 ? (
                <TableRow
                  sx={{ "&:hover": { bgcolor: "transparent !important" } }}
                >
                  <TableCell sx={{ p: 1, py: 2 }} colSpan={colSpan}>
                    <TablePagination
                      gotoPage={gotoPage}
                      rows={rows}
                      setPageSize={setPageSize}
                      pageSize={pageSize}
                      pageIndex={pageIndex}
                    />
                  </TableCell>
                </TableRow>
              ) : (
                <EmptyTable
                  msg={isLoading ? loadingMessage : emptyTableMessage}
                  colSpan={colSpan}
                />
              )}
            </TableBody>
          </Table>
        </Stack>
      </ScrollX>
    </MainCard>
  );
}

export function ReactSubTable({
  columns,
  data,
  loading,
}: {
  columns: Column[];
  data: any;
  loading: boolean;
}) {
  const {
    getTableProps,
    getTableBodyProps,
    setHiddenColumns,
    headerGroups,
    rows,
    prepareRow,
  } = useTable({
    columns,
    data,
    initialState: {
      hiddenColumns: ["avatar", "email"],
    },
  });
  const theme = useTheme();
  const matchDownSM = useMediaQuery(theme.breakpoints.down("sm"));
  useEffect(() => {
    if (matchDownSM) {
      setHiddenColumns([
        "age",
        "contact",
        "visits",
        "email",
        "status",
        "avatar",
      ]);
    } else {
      setHiddenColumns(["avatar", "email"]);
    }
    // eslint-disable-next-line
  }, [matchDownSM]);

  if (loading) {
    return (
      <Table {...getTableProps()}>
        <TableHead>
          {headerGroups.map((headerGroup) => (
            <TableRow {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <TableCell sx={{ p: 0.8 }} {...column.getHeaderProps()}>
                  {column.render("Header")}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableHead>
        <TableBody {...getTableBodyProps()}>
          {[0, 1, 2].map((item: number) => (
            <TableRow key={item}>
              {[0, 1, 2, 3, 4, 5].map((col: number) => (
                <TableCell sx={{ p: 0.9 }} key={col}>
                  <Skeleton animation="wave" />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }

  return (
    <Table {...getTableProps()}>
      <TableHead>
        {headerGroups.map((headerGroup) => (
          <TableRow {...headerGroup.getHeaderGroupProps()}>
            {headerGroup.headers.map((column: HeaderGroup) => (
              <TableCell
                sx={{ p: 0.8 }}
                {...column.getHeaderProps([{ className: column.className }])}
              >
                {column.render("Header")}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableHead>
      <TableBody {...getTableBodyProps()}>
        {rows.length === 0 ? (
          <EmptyTable msg="No Orders Found" colSpan={10} />
        ) : (
          <>
            {rows.map((row) => {
              prepareRow(row);
              return (
                <TableRow {...row.getRowProps()}>
                  {row.cells.map((cell: Cell) => (
                    <TableCell
                      sx={{ p: 0.9 }}
                      {...cell.getCellProps([
                        { className: cell.column.className },
                      ])}
                    >
                      {cell.render("Cell")}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </>
        )}
      </TableBody>
    </Table>
  );
}

interface ReactTableWithSubTableBackendPaginationProps {
  columns: Column[];
  data: any[];
  renderRowSubComponent: FC<any>;
  colSpan: number;
  rowClick: boolean;
  title: string;
  emptyTableMessage: string;
  isLoading: boolean;
  loadingMessage: string;
  pageState: PaginationState;
  setPage: (val: PaginationState) => void;
  totalNumberOfRecords: number;
}

export function ReactTableWithSubTableBackEndPagination({
  columns,
  data,
  renderRowSubComponent,
  colSpan,
  rowClick,
  title,
  emptyTableMessage,
  loadingMessage,
  isLoading,
  pageState,
  setPage,
  totalNumberOfRecords,
}: ReactTableWithSubTableBackendPaginationProps) {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    page,
    visibleColumns,
    state: { pageIndex, expanded },
  } = useTable(
    {
      columns,
      data,
      initialState: {
        pageIndex: pageState.page,
        pageSize: pageState.limit,
        hiddenColumns: ["avatar", "quantity"],
      },
      manualPagination: true,
      pageCount: Math.ceil(totalNumberOfRecords / pageState.limit),
    },
    useGlobalFilter,
    useFilters,
    useSortBy,
    useExpanded,
    usePagination
  );

  const handlePageChange = (pageIndex: number) => {
    setPage({ ...pageState, page: pageIndex });
  };

  const handlePageSizeChange = (pageSize: number) => {
    setPage({ ...pageState, limit: pageSize, page: 0 });
  };

  return (
    <MainCard content={false}>
      <ScrollX>
        <Stack spacing={3}>
          <Box sx={{ p: 3, pb: 0 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1}
            >
              <Typography variant="h5">{title}</Typography>
            </Stack>
          </Box>

          <Table {...getTableProps()}>
            <TableHead>
              {headerGroups.map((headerGroup: HeaderGroup<{}>) => {
                const { key, ...restProps } = headerGroup.getHeaderGroupProps();
                return (
                  <TableRow
                    {...restProps}
                    key={key}
                    sx={{ "& > th:first-of-type": { width: "58px" } }}
                  >
                    {headerGroup.headers.map((column: HeaderGroup) => {
                      const { key, ...restProps } = column.getHeaderProps([
                        { className: column.className },
                      ]);
                      return (
                        <TableCell sx={{ p: 0.6 }} {...restProps} key={key}>
                          {column.render("Header")}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableHead>
            <TableBody {...getTableBodyProps()}>
              {page.map((row: Row, i: number) => {
                prepareRow(row);
                const rowProps = row.getRowProps();
                const { key, ...restProps } = rowProps;
                return (
                  <Fragment key={i}>
                    <TableRow
                      {...restProps}
                      key={key}
                      sx={{ cursor: "pointer" }}
                      onClick={(e: MouseEvent<HTMLTableRowElement>) => {
                        if (rowClick) {
                          e.stopPropagation();
                          row.toggleRowExpanded();
                        }
                      }}
                    >
                      {row.cells.map((cell: Cell) => {
                        const { key, ...restProps } = cell.getCellProps([
                          { className: cell.column.className },
                        ]);
                        return (
                          <TableCell sx={{ p: 0.5 }} {...restProps} key={key}>
                            {cell.render("Cell")}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                    {row.isExpanded &&
                      renderRowSubComponent({
                        row,
                        rowProps,
                        visibleColumns,
                        expanded,
                      })}
                  </Fragment>
                );
              })}
              {data.length > 0 ? (
                <TableRow
                  sx={{ "&:hover": { bgcolor: "transparent !important" } }}
                >
                  <TableCell sx={{ p: 1, py: 2 }} colSpan={colSpan}>
                    <TablePagination
                      totalPages={Math.ceil(
                        totalNumberOfRecords / pageState.limit
                      )}
                      gotoPage={handlePageChange}
                      rows={rows}
                      setPageSize={handlePageSizeChange}
                      pageSize={pageState.limit}
                      pageIndex={pageIndex}
                      initialPageSize={pageState.limit}
                    />
                  </TableCell>
                </TableRow>
              ) : (
                <EmptyTable
                  msg={isLoading ? loadingMessage : emptyTableMessage}
                  colSpan={colSpan}
                />
              )}
            </TableBody>
          </Table>
        </Stack>
      </ScrollX>
    </MainCard>
  );
}

interface ReactTableWithSubTableFrontendPaginationProps {
  columns: Column<any>[];
  data: any[]; // Full data for current table
  renderRowSubComponent: FC<any>;
  colSpan: number;
  rowClick: boolean;
  title: string;
  emptyTableMessage: string;
  isLoading: boolean;
  loadingMessage: string;
  defaultPageSize?: number;
  expanded?: Record<string, boolean>;
  onExpandedChange?: React.Dispatch<
    React.SetStateAction<Record<string, boolean>>
  >;
}

export function ReactTableWithSubTableFrontEndPagination({
  columns,
  data,
  renderRowSubComponent,
  colSpan,
  rowClick,
  title,
  emptyTableMessage,
  loadingMessage,
  isLoading,
  defaultPageSize = 10,
  expanded,
  onExpandedChange,
}: ReactTableWithSubTableFrontendPaginationProps) {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    visibleColumns,
    state: { pageIndex, pageSize },
    setPageSize,
    gotoPage,
    rows,
  } = useTable<any>(
    {
      columns,
      data,
      expanded,
      onExpandedChange,
      autoResetExpanded: false,
      getRowId: (row) => row.parentOrder?.orderId?.toString(),
      initialState: {
        pageIndex: 0,
        pageSize: defaultPageSize,
      },
      stateReducer: (newState, action, prevState) => {
        return {
          ...newState,
          expanded: expanded ?? {},
        };
      },
    },
    useGlobalFilter,
    useFilters,
    useSortBy,
    useExpanded,
    usePagination
  );

  return (
    <MainCard content={false}>
      <ScrollX>
        <Stack spacing={3}>
          <Box sx={{ p: 3, pb: 0 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1}
            >
              <Typography variant="h5">{title}</Typography>
            </Stack>
          </Box>

          <Table {...getTableProps()}>
            <TableHead>
              {headerGroups.map((headerGroup: HeaderGroup<{}>) => {
                const { key, ...restProps } = headerGroup.getHeaderGroupProps();
                return (
                  <TableRow
                    {...restProps}
                    key={key}
                    sx={{ "& > th:first-of-type": { width: "58px" } }}
                  >
                    {headerGroup.headers.map((column: HeaderGroup) => {
                      const { key, ...restProps } = column.getHeaderProps([
                        { className: column.className },
                      ]);
                      return (
                        <TableCell sx={{ p: 0.6 }} {...restProps} key={key}>
                          {column.render("Header")}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableHead>
            <TableBody {...getTableBodyProps()}>
              {page.map((row: Row, i: number) => {
                prepareRow(row);
                const rowProps = row.getRowProps();
                const { key, ...restProps } = rowProps;
                return (
                  <Fragment key={i}>
                    <TableRow
                      {...restProps}
                      key={key}
                      sx={{ cursor: "pointer" }}
                      onClick={(e: MouseEvent<HTMLTableRowElement>) => {
                        if (rowClick) {
                          e.stopPropagation();
                          onExpandedChange?.((prev) => ({
                            ...prev,
                            [row.id]: !prev?.[row.id],
                          }));
                        }
                      }}
                    >
                      {row.cells.map((cell: Cell) => {
                        const { key, ...restProps } = cell.getCellProps([
                          { className: cell.column.className },
                        ]);
                        return (
                          <TableCell sx={{ p: 0.5 }} {...restProps} key={key}>
                            {cell.render("Cell")}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                    {expanded?.[row.id] &&
                      renderRowSubComponent({
                        row,
                        rowProps,
                        visibleColumns,
                        expanded,
                      })}
                  </Fragment>
                );
              })}
              {data.length > 0 ? (
                <>
                  {data.length > pageSize && (
                    <TableRow
                      sx={{ "&:hover": { bgcolor: "transparent !important" } }}
                    >
                      <TableCell sx={{ p: 1, py: 2 }} colSpan={colSpan}>
                        <TablePagination
                          totalPages={Math.ceil(data.length / pageSize)}
                          gotoPage={gotoPage}
                          rows={rows}
                          setPageSize={setPageSize}
                          pageSize={pageSize}
                          pageIndex={pageIndex}
                          initialPageSize={defaultPageSize}
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </>
              ) : (
                <EmptyTable
                  msg={isLoading ? loadingMessage : emptyTableMessage}
                  colSpan={colSpan}
                />
              )}
            </TableBody>
          </Table>
        </Stack>
      </ScrollX>
    </MainCard>
  );
}
